<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import { useStringFunctions, useSchemaValidation } from '@apparatix/composables';
import { type PendingContact } from '@apparatix/schemas/Contact';
import { type WizardNewContact, wizardNewContact } from '@apparatix/schemas/CreateLeadWizard';
import { PendingAccount } from '@apparatix/schemas/Account';
import {
  Column,
  Columns,
  InputGroup,
  Stack,
  Toolbar,
  Typography,
} from '@apparatix/library/core';
import AccountSearchAutocomplete from './AccountSearchAutocomplete.vue';
import ContactAutoComplete from './ContactAutoComplete.vue';
import CollapsableContent from '@apparatix/library/CollapsableContent.vue';
import type { Address } from '@apparatix/types/Contacts';
import type { Address as AccountAddress } from '@apparatix/types/Account';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';

type Props = {
  useDynamicNameSearch?: boolean;
  useDynamicCompanySearch?: boolean;
  isAssociatedWithExistingAccount?: boolean;
  allowAgencyInWizard?: boolean;
  address?: Address | AccountAddress | null;
  companyName?: string;
  restorePrefilledFields?: number;
  isOpportunityWizard?: boolean;
  hideOkCancel?: boolean;
  submitLabel?: string
}

type Emits = {
  (e: 'on:create', data: WizardNewContact): void;
  (e: 'contact-selected', selection: WizardNewContact): void;
  (e: 'cancel'): void;
  (e: 'on:dirty-change', isDirty: boolean): void;
  (e: 'on:account-select', data): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const confirm = useConfirm();

function getInitialAddressValues(addr) {
  return addr
    ? {
      addressLine1: addr.address_line1 ?? addr.addressLine1 ?? '',
      addressLine2: addr.address_line2 ?? addr.addressLine2 ?? '',
      addressLine3: addr.address_line3 ?? addr.addressLine3 ?? '',
      city: addr.city ?? '',
      region: addr.region ?? '',
      postalCode: addr.postal_code ?? addr.postalCode ?? '',
    }
    : {};
}

onMounted(() => {
  contact.value.addresses[0] = getInitialAddressValues(props.address);
  contact.value.account.name = props.companyName ?? '';
});

const applyAddress = (addr?: Address | AccountAddress | null) => {
  if (addr) {
    contact.value.addresses[0] = getInitialAddressValues(addr);
  }
};

const applyCompanyName = (name?: string | null) => {
  if (name) {
    contact.value.account.name = name;
  }
};

watch(() => props.address, applyAddress);

watch(() => props.companyName, applyCompanyName);

watch(
  () => props.restorePrefilledFields,
  (restore) => {
    if (restore) {
      applyAddress(props.address);
      applyCompanyName(props.companyName);
    }
  },
);

const contact = defineModel<WizardNewContact>();

const agencyCheckboxId = ref(crypto.randomUUID());
const accountAutocompleteInstance = ref();
const nameAutocompleteInstance = ref();
const contactAutocompleteValue = ref<PendingContact>({ fullName: '' });

const accountAutocompleteValue = ref<any>();

const {
  dirty, errors, valid, reset,
} = useSchemaValidation(wizardNewContact, contact);
const { isEmptyString } = useStringFunctions();

watch(() => dirty.value, () => emit('on:dirty-change', dirty.value));

const disableAgencyCheckbox = computed(() =>
  isEmptyString(contact.value?.account?.name, true) ||
  Boolean(contact.value?.account?.id) ||
  contact.value?.isEditing && Boolean(contact.value?.account?.type),
);

const saveButtonLabel = computed(() => {
  if (!contact.value.isEditing) return props.isOpportunityWizard ? 'Add' : 'Add Contact';

  if (isEmptyString(contact.value.fullName, true) && isEmptyString(contact.value.companyName, true)) {
    return 'Update';
  }

  if (!isEmptyString(contact.value.fullName, true)) {
    return `Update ${contact.value.fullName}`;
  }

  return `Update ${contact.value.companyName}`;
});

const companyIsAgency = computed({
  get() {
    return contact.value?.account?.type === 'Agency';
  },
  set(val: boolean) {
    if (val) {
      contact.value.account.type = 'Agency';
    } else {
      contact.value.account.type = 'Lead';
    }
  },
});

watch(() => contactAutocompleteValue.value, () => {
  if (typeof contactAutocompleteValue.value === 'object' && contactAutocompleteValue.value?.id) {
    emit('contact-selected', {
      ...contactAutocompleteValue.value,
      key: crypto.randomUUID(),
      account: {
        name: undefined,
      },
    });

    resetForm();
  } else {
    contact.value.fullName = contactAutocompleteValue.value?.fullName;
  }
});

watch(() => accountAutocompleteValue.value, () => {
  if (typeof accountAutocompleteValue.value === 'object' && accountAutocompleteValue.value?.id) {
    emit('on:account-select', {
      ...accountAutocompleteValue.value,
    });

    resetForm();
  } else {
    contact.value.account.name = accountAutocompleteValue.value.name;
  }
});

const onCreateClick = () => {
  if (!valid.value) {
    if (errors.value.emails) {
      confirm.require({
        message: String(errors.value.emails),
        group: 'centered-error-lead-wizard-contact',
        acceptLabel: 'Ok',
        rejectClass: '!hidden',
        modal: false,
        acceptProps: {
          size: 'small',
          variant: 'outlined',
        },
      });
    }
    return;
  }

  if (!contact.value.account) {
    contact.value.account = {
      name: undefined,
    };
  }

  if (contact.value.account?.name) {
    contact.value.companyName = contact.value.account.name;
  }

  if (!isEmptyString(contact.value.account?.name, true) && !contact.value.account?.id && !contact.value.account?.type) {
    contact.value.account.type = 'Advertiser';
  }
  emit('on:create', { ...contact.value, isEditing: false });
  resetForm();
};

const onCancelClick = () => {
  if (!contact.value.isEditing) {
    resetForm();
  }
  emit('cancel');
};

const resetForm = () => {
  if (typeof accountAutocompleteInstance.value?.reset === 'function') {
    accountAutocompleteInstance.value.reset();
  }
  if (typeof nameAutocompleteInstance.value?.reset === 'function') {
    nameAutocompleteInstance.value.reset();
  }
  contactAutocompleteValue.value = { fullName: '' };
  contact.value.addresses[0] = getInitialAddressValues(props.address);
  contact.value.account.name = props.companyName ?? '';
  reset();
};
</script>
<template>
  <Stack :gap="5">
    <template v-if="props.isOpportunityWizard">
      <InputGroup>
        <Typography variant="form-label">
          Company
        </Typography>
        <AccountSearchAutocomplete
          v-if="props.useDynamicCompanySearch"
          ref="accountAutocompleteInstance"
          v-model="accountAutocompleteValue"
          placeholder="Search or enter Account name"
          :invalid="Boolean(errors.fullName)"
          :disabled="props.isAssociatedWithExistingAccount && contact.isEditing"
          :is-opportunity-wizard="props.isOpportunityWizard"
        />
        <IconField v-if="!props.useDynamicCompanySearch">
          <InputIcon class="pi pi-building" />
          <InputText
            v-model="contact.account.name"
            placeholder="Account name"
            :invalid="Boolean(errors.fullName)"
            :disabled="props.isAssociatedWithExistingAccount && contact.isEditing"
            fluid
          />
        </IconField>
        <template v-if="props.allowAgencyInWizard">
          <Toolbar align="left">
            <Checkbox
              v-model="companyIsAgency"
              :disabled="disableAgencyCheckbox"
              :input-id="agencyCheckboxId"
              binary
            />
            <Typography variant="form-label" class="cursor-pointer" :for="agencyCheckboxId">
              This is an Agency
            </Typography>
          </Toolbar>
        </template>
      </InputGroup>
      <InputGroup>
        <Typography variant="form-label">
          Contact Name
        </Typography>
        <ContactAutoComplete
          v-if="props.useDynamicNameSearch"
          ref="nameAutocompleteInstance"
          v-model="contactAutocompleteValue"
          placeholder="Search or enter Contact name"
          attach-account-object
        />
        <IconField v-if="!props.useDynamicNameSearch">
          <InputIcon class="pi pi-id-card" />
          <InputText
            v-model="contact.fullName"
            placeholder="Contact name"
            :invalid="Boolean(errors.fullName)"
            fluid
          />
        </IconField>
        <Typography v-if="errors.fullName" variant="form-error">
          {{ errors.fullName }}
        </Typography>
      </InputGroup>
    </template>
    <template v-else>
      <InputGroup>
        <Typography variant="form-label">
          Name
        </Typography>
        <ContactAutoComplete
          v-if="props.useDynamicNameSearch"
          ref="nameAutocompleteInstance"
          v-model="contactAutocompleteValue"
          placeholder="Search or enter Contact name"
          attach-account-object
        />
        <IconField v-if="!props.useDynamicNameSearch">
          <InputIcon class="pi pi-id-card" />
          <InputText
            v-model="contact.fullName"
            placeholder="Contact name"
            :invalid="Boolean(errors.fullName)"
            fluid
          />
        </IconField>
        <Typography v-if="errors.fullName" variant="form-error">
          {{ errors.fullName }}
        </Typography>
      </InputGroup>
      <InputGroup>
        <Typography variant="form-label">
          Company
        </Typography>
        <AccountSearchAutocomplete
          v-if="props.useDynamicCompanySearch"
          ref="accountAutocompleteInstance"
          v-model="(contact.account as PendingAccount)"
          placeholder="Search or enter Account name"
          :invalid="Boolean(errors.fullName)"
          :disabled="props.isAssociatedWithExistingAccount && contact.isEditing"
          :is-opportunity-wizard="props.isOpportunityWizard"
        />
        <IconField v-if="!props.useDynamicCompanySearch">
          <InputIcon class="pi pi-building" />
          <InputText
            v-model="contact.account.name"
            placeholder="Account name"
            :invalid="Boolean(errors.fullName)"
            :disabled="props.isAssociatedWithExistingAccount && contact.isEditing"
            fluid
          />
        </IconField>
        <template v-if="props.allowAgencyInWizard">
          <Toolbar align="left">
            <Checkbox
              v-model="companyIsAgency"
              :disabled="disableAgencyCheckbox"
              :input-id="agencyCheckboxId"
              binary
            />
            <Typography variant="form-label" class="cursor-pointer" :for="agencyCheckboxId">
              This is an Agency
            </Typography>
          </Toolbar>
        </template>
      </InputGroup>
    </template>

    <template v-if="!props.isOpportunityWizard">
      <InputGroup>
        <Typography variant="form-label">
          Email
        </Typography>
        <IconField>
          <InputIcon class="pi pi-envelope" />
          <InputText v-model="contact.emails[0].email" fluid />
        </IconField>
      </InputGroup>
      <InputGroup>
        <Typography variant="form-label">
          Phone
        </Typography>
        <IconField>
          <InputIcon class="pi pi-phone" />
          <InputText v-model="contact.phoneNumbers[0].number" fluid />
        </IconField>
      </InputGroup>
    </template>
    <CollapsableContent :label="props.isOpportunityWizard ? 'contact fields' : 'address fields'">
      <template v-if="props.isOpportunityWizard">
        <InputGroup>
          <Typography variant="form-label">
            Email
          </Typography>
          <IconField>
            <InputIcon class="pi pi-envelope" />
            <InputText v-model="contact.emails[0].email" fluid />
          </IconField>
        </InputGroup>
        <InputGroup>
          <Typography variant="form-label">
            Phone
          </Typography>
          <IconField>
            <InputIcon class="pi pi-phone" />
            <InputText v-model="contact.phoneNumbers[0].number" fluid />
          </IconField>
        </InputGroup>
      </template>
      <InputGroup>
        <Typography variant="form-label">
          Address
        </Typography>
        <IconField>
          <InputIcon class="pi pi-map-marker" />
          <InputText v-model="contact.addresses[0].addressLine1" fluid />
        </IconField>
      </InputGroup>
      <InputGroup>
        <Typography variant="form-label">
          Address Line 2
        </Typography>
        <IconField>
          <InputIcon class="pi pi-map-marker" />
          <InputText v-model="contact.addresses[0].addressLine2" fluid />
        </IconField>
      </InputGroup>
      <InputGroup>
        <Typography variant="form-label">
          City
        </Typography>
        <InputText v-model="contact.addresses[0].city" fluid />
      </InputGroup>
      <Columns>
        <Column>
          <InputGroup>
            <Typography variant="form-label">
              State
            </Typography>
            <InputText v-model="contact.addresses[0].region" fluid />
          </InputGroup>
        </Column>
        <Column>
          <InputGroup>
            <Typography variant="form-label">
              Zip
            </Typography>
            <InputText v-model="contact.addresses[0].postalCode" fluid />
          </InputGroup>
        </Column>
      </Columns>
    </CollapsableContent>
    <Toolbar v-if="!props.hideOkCancel" align="left">
      <Button :label="saveButtonLabel" :disabled="!dirty" @click="onCreateClick" />
      <Button
        label="Cancel"
        :disabled="!dirty"
        severity="secondary"
        @click="onCancelClick"
      />
    </Toolbar>
  </Stack>
  <ConfirmDialog group="centered-error-lead-wizard-contact" />
</template>
